import { TimePatientArmEvaluationState } from '@tempus/trials-types';

import { createTranslator, type LocaleCode, type MessageKey, type Translate } from '~/i18n';

function localized<T>(locale: LocaleCode, build: (t: Translate) => T): T {
  return build(createTranslator(locale));
}

import type { TIMEScreeningState } from '../api/types/timeScreeningStore';

// Enums
export enum ScreenFor {
  careGaps = 'careGaps',
  trials = 'trials',
}

export enum MatchingTab {
  URGENT = 'Urgent',
  UNSCREENED = 'Unscreened',
  RESCREEN = 'Re-screen',
  SCREEN_LATER = 'Screen later',
  CASE_REVIEW = 'Case review',
}

export enum TableSortValue {
  MATCH_SCORE_HIGHEST = 'matchScoreHighest',
  MATCH_SCORE_LOWEST = 'matchScoreLowest',
  UPCOMING_VISIT_SOONEST = 'upcomingVisitSoonest',
  UPCOMING_VISIT_FURTHEST = 'upcomingVisitFurthest',
  REPORT_DATE_LATEST = 'reportDateLatest',
  REPORT_DATE_OLDEST = 'reportDateOldest',
  ENCOUNTER_DATE_LATEST = 'encounterDateLatest',
  ENCOUNTER_DATE_OLDEST = 'encounterDateOldest',
  RESCREEN_DATE_SOONEST = 'rescreenDateSoonest',
  RESCREEN_DATE_FURTHEST = 'rescreenDateFurthest',
  PATIENT_ID_AZ = 'patientIdAZ',
  PATIENT_ID_ZA = 'patientIdZA',
  COHORT_AZ = 'cohortAZ',
  COHORT_ZA = 'cohortZA',
  DIAGNOSIS_DATE_LATEST = 'diagnosisDateLatest',
  DIAGNOSIS_DATE_OLDEST = 'diagnosisDateOldest',
  NEWEST = 'newest',
  OLDEST = 'oldest',
}

// Constants for location statuses
const PENDING_TIME_LOCATIONS = 'Pending TIME locations';
const ENROLLING_TIME_LOCATIONS = 'Enrolling TIME locations';
const REFERRING_TIME_LOCATIONS = 'Referring TIME locations';
const TRIAL_ACTIVE_TIME_LOCATIONS = 'Trial Active TIME locations';

// Option arrays
export const locationStatusOpts = [
  { label: PENDING_TIME_LOCATIONS, value: PENDING_TIME_LOCATIONS },
  { label: ENROLLING_TIME_LOCATIONS, value: ENROLLING_TIME_LOCATIONS },
  { label: REFERRING_TIME_LOCATIONS, value: REFERRING_TIME_LOCATIONS },
  { label: TRIAL_ACTIVE_TIME_LOCATIONS, value: TRIAL_ACTIVE_TIME_LOCATIONS },
];

const locationStatusLabelKey: Record<string, MessageKey> = {
  [PENDING_TIME_LOCATIONS]: 'tables.locationStatus.pending',
  [ENROLLING_TIME_LOCATIONS]: 'tables.locationStatus.enrolling',
  [REFERRING_TIME_LOCATIONS]: 'tables.locationStatus.referring',
  [TRIAL_ACTIVE_TIME_LOCATIONS]: 'tables.locationStatus.trialActive',
};

/** Localized labels. Values stay the English status strings the API stores. */
export const getLocationStatusOpts = (locale: LocaleCode) =>
  localized(locale, (t) =>
    locationStatusOpts.map((option) => ({
      value: option.value,
      label: t(locationStatusLabelKey[option.value]),
    })),
  );

export const decisionOpts = [
  { label: 'Pass', value: TimePatientArmEvaluationState.PASSED_PRESCREEN },
  { label: 'Fail', value: TimePatientArmEvaluationState.FAILED_PRESCREEN },
];

/** Localized Pass/Fail labels. Values stay the prescreen enum. */
export const getDecisionOpts = (locale: LocaleCode) =>
  localized(locale, (t) => [
    { label: t('tables.trialScreened.pass'), value: TimePatientArmEvaluationState.PASSED_PRESCREEN },
    { label: t('tables.trialScreened.fail'), value: TimePatientArmEvaluationState.FAILED_PRESCREEN },
  ]);

export const lookbackFilterOpts = [
  { label: '2 weeks', value: '0.5' },
  { label: '1 month', value: '1' },
  { label: '2 months', value: '2' },
  { label: '3 months', value: '3' },
  { label: '6 months', value: '6' },
  { label: '12 months', value: '12' },
  { label: '15 months', value: '15' },
  { label: '18 months', value: '18' },
  { label: '24 months', value: '24' },
  { label: '30 months', value: '30' },
  { label: 'No filter selected', value: '0' },
];

// Upcoming visit filter types
export const WITHIN = 'Within';
export const BEFORE_SPECIFIED_DATE = 'Before specified date';

export const upcomingVisitFilterTypeOpts = [
  { label: WITHIN, value: WITHIN },
  { label: BEFORE_SPECIFIED_DATE, value: BEFORE_SPECIFIED_DATE },
];

export const upcomingVisitWithinOpts = [
  { label: '1 week', value: '7' },
  { label: '2 weeks', value: '14' },
  { label: '1 month', value: '30' },
  { label: 'No filter selected', value: '-1' },
];

// Table sort options
export const matchingTableSortOptsBase = [
  {
    label: 'Match score (highest first)',
    value: TableSortValue.MATCH_SCORE_HIGHEST,
  },
  {
    label: 'Match score (lowest first)',
    value: TableSortValue.MATCH_SCORE_LOWEST,
  },
  {
    label: 'Upcoming visit (soonest first)',
    value: TableSortValue.UPCOMING_VISIT_SOONEST,
  },
  {
    label: 'Upcoming visit (furthest first)',
    value: TableSortValue.UPCOMING_VISIT_FURTHEST,
  },
  {
    label: 'Report date (latest first)',
    value: TableSortValue.REPORT_DATE_LATEST,
  },
  {
    label: 'Report date (oldest first)',
    value: TableSortValue.REPORT_DATE_OLDEST,
  },
  {
    label: 'Encounter date (latest first)',
    value: TableSortValue.ENCOUNTER_DATE_LATEST,
  },
  {
    label: 'Encounter date (oldest first)',
    value: TableSortValue.ENCOUNTER_DATE_OLDEST,
  },
];

export const matchingCareGapTableSortOptsBase = [
  { label: 'Report date (latest first)', value: TableSortValue.REPORT_DATE_LATEST },
  { label: 'Report date (oldest first)', value: TableSortValue.REPORT_DATE_OLDEST },
  { label: 'Diagnosis date (latest first)', value: TableSortValue.DIAGNOSIS_DATE_LATEST },
  { label: 'Diagnosis date (oldest first)', value: TableSortValue.DIAGNOSIS_DATE_OLDEST },
];

export const screenedTableSortOptsBase = [
  { label: 'Newest', value: TableSortValue.NEWEST },
  { label: 'Oldest', value: TableSortValue.OLDEST },
  {
    label: 'Match score (highest first)',
    value: TableSortValue.MATCH_SCORE_HIGHEST,
  },
  {
    label: 'Match score (lowest first)',
    value: TableSortValue.MATCH_SCORE_LOWEST,
  },
];

export const screenForOptions = [
  { label: 'Trials', value: 'trials' },
  { label: 'Care gaps', value: 'careGaps' },
];

/** Localized screen-for labels. Values stay `trials` / `careGaps`. */
export const getScreenForOptions = (locale: LocaleCode) =>
  localized(locale, (t) => [
    { label: t('tables.screenFor.trials'), value: ScreenFor.trials },
    { label: t('tables.screenFor.careGaps'), value: ScreenFor.careGaps },
  ]);

export const getMatchingTableSortOpts = (locale: LocaleCode) =>
  localized(locale, (t) => [
    { label: t('tables.sort.matchScoreHighest'), value: TableSortValue.MATCH_SCORE_HIGHEST },
    { label: t('tables.sort.matchScoreLowest'), value: TableSortValue.MATCH_SCORE_LOWEST },
    { label: t('tables.sort.upcomingVisitSoonest'), value: TableSortValue.UPCOMING_VISIT_SOONEST },
    { label: t('tables.sort.upcomingVisitFurthest'), value: TableSortValue.UPCOMING_VISIT_FURTHEST },
    { label: t('tables.sort.reportDateLatest'), value: TableSortValue.REPORT_DATE_LATEST },
    { label: t('tables.sort.reportDateOldest'), value: TableSortValue.REPORT_DATE_OLDEST },
    { label: t('tables.sort.encounterDateLatest'), value: TableSortValue.ENCOUNTER_DATE_LATEST },
    { label: t('tables.sort.encounterDateOldest'), value: TableSortValue.ENCOUNTER_DATE_OLDEST },
  ]);

export const getMatchingCareGapTableSortOpts = (locale: LocaleCode) =>
  localized(locale, (t) => [
    { label: t('tables.sort.reportDateLatest'), value: TableSortValue.REPORT_DATE_LATEST },
    { label: t('tables.sort.reportDateOldest'), value: TableSortValue.REPORT_DATE_OLDEST },
    { label: t('tables.sort.diagnosisDateLatest'), value: TableSortValue.DIAGNOSIS_DATE_LATEST },
    { label: t('tables.sort.diagnosisDateOldest'), value: TableSortValue.DIAGNOSIS_DATE_OLDEST },
  ]);

export const getScreenedTableSortOpts = (locale: LocaleCode) =>
  localized(locale, (t) => [
    { label: t('tables.sort.newest'), value: TableSortValue.NEWEST },
    { label: t('tables.sort.oldest'), value: TableSortValue.OLDEST },
    { label: t('tables.sort.matchScoreHighest'), value: TableSortValue.MATCH_SCORE_HIGHEST },
    { label: t('tables.sort.matchScoreLowest'), value: TableSortValue.MATCH_SCORE_LOWEST },
  ]);

export const matchOptionByValue = <T extends { value: unknown }>(
  options: T[],
  selected?: { value: unknown } | null,
): T => options.find((option) => option.value === selected?.value) ?? options[0];

// Default values
export const defaultTabCounts = {
  [MatchingTab.URGENT]: 0,
  [MatchingTab.UNSCREENED]: 0,
  [MatchingTab.RESCREEN]: 0,
  [MatchingTab.SCREEN_LATER]: 0,
  [MatchingTab.CASE_REVIEW]: 0,
};

export const defaultCareGapFilters: TIMEScreeningState['careGap']['filters'] = {
  careGapType: null,
  decision: null,
  location: '',
  noteText: '',
  page: 1,
  pageSize: 15,
  pathway: null,
  patientLinkageId: '',
  physician: '',
  sites: [],
  sortBy: matchingCareGapTableSortOptsBase[0],
  stage: null,
  tab: MatchingTab.UNSCREENED,
};

export const defaultTrialFilters: TIMEScreeningState['trials']['filters'] = {
  cohort: null,
  emrEncounterLookback: lookbackFilterOpts[lookbackFilterOpts.length - 1],
  lastUpdated: null,
  locations: [],
  locationStatuses: locationStatusOpts,
  matchScoreMin: '0.0',
  page: 1,
  pageSize: 15,
  patientLinkageId: '',
  sites: [],
  sortBy: matchingTableSortOptsBase[0],
  tab: MatchingTab.UNSCREENED,
  tempusReportLookback: lookbackFilterOpts[lookbackFilterOpts.length - 1],
  trial: null,
  upcomingVisitBefore: '',
  upcomingVisitFilterType: upcomingVisitFilterTypeOpts[0],
  upcomingVisitWithin: upcomingVisitWithinOpts[upcomingVisitWithinOpts.length - 1],
  // Screening Filters
  trials: [],
  patientId: '',
  account: '',
  locationStatus: [],
  noteText: '',
  decision: null,
};
